// ==================== PRIVACY POLICY PAGE COMPONENT ====================

import React, { useEffect, useState } from "react";
import Footer from "./Footer.js";
import Headerpage from "./Header2.js";
import { getData ,serverURL } from "../../../Services/Fetchnodeservices.js";
import {
  Shield,
  Lock,
  Eye,
  Database,
  FileText,
  CheckCircle,
  Clock,
  Mail,
  Phone,
  Globe,
  AlertCircle,
} from "lucide-react";

const PrivacyPolicy = ({ isDark }) => {
  const [privacyData, setPrivacyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Theme colors
  const t = isDark
    ? {
        bg: "#0a0c10",
        cardBg: "#141824",
        text: "#e2e8f0",
        textSecondary: "#94a3b8",
        border: "rgba(255,255,255,0.08)",
        heading: "#ffffff",
        code: "#1e293b",
        accent: "#7c3aed",
      }
    : {
        bg: "#f8fafc",
        cardBg: "#ffffff",
        text: "#334155",
        textSecondary: "#64748b",
        border: "rgba(0,0,0,0.1)",
        heading: "#0f172a",
        code: "#f1f5f9",
        accent: "#7c3aed",
      };

  // Fetch privacy policy from backend
  const fetchPrivacyPolicy = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getData("privacypolicy/cmspages");
      console.log("API Response:", response);

      if (response?.status && response?.data) {
        setPrivacyData(response.data);
      } else {
        setError("Privacy policy not found");
      }
    } catch (err) {
      console.error("Error fetching privacy policy:", err);
      setError("Failed to load privacy policy. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrivacyPolicy();
  }, []);

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div
        style={{
          fontFamily: "'Poppins', sans-serif",
          background: t.bg,
          minHeight: "100vh",
        }}
      >
        <Headerpage />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: "60px",
                height: "60px",
                border: `3px solid ${t.border}`,
                borderTopColor: t.accent,
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 20px",
              }}
            />
            <p style={{ color: t.textSecondary }}>Loading privacy policy...</p>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          fontFamily: "'Poppins', sans-serif",
          background: t.bg,
          minHeight: "100vh",
        }}
      >
        <Headerpage />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            padding: "20px",
          }}
        >
          <div
            style={{
              textAlign: "center",
              backgroundColor: t.cardBg,
              padding: "40px",
              borderRadius: "20px",
              border: `1px solid ${t.border}`,
              maxWidth: "500px",
            }}
          >
            <div
              style={{
                width: "80px",
                height: "80px",
                background: "#fee2e2",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px",
              }}
            >
              <AlertCircle size={40} color="#ef4444" />
            </div>
            <h2 style={{ color: t.heading, marginBottom: "12px" }}>
              Privacy Policy Not Found
            </h2>
            <p style={{ color: t.textSecondary, marginBottom: "24px" }}>
              {error}
            </p>
            <button
              onClick={fetchPrivacyPolicy}
              style={{
                padding: "12px 24px",
                backgroundColor: t.accent,
                color: "white",
                border: "none",
                borderRadius: "12px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              Try Again
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <style>{`
        .privacy-content {
          word-wrap: break-word;
        }
        .privacy-content h1 {
          font-size: 2rem;
          margin-top: 1.5em;
          margin-bottom: 0.5em;
          color: ${t.heading};
          font-weight: 700;
        }
        .privacy-content h2 {
          font-size: 1.5rem;
          margin-top: 1.5em;
          margin-bottom: 0.5em;
          color: ${t.heading};
          font-weight: 600;
          border-left: 4px solid ${t.accent};
          padding-left: 1rem;
        }
        .privacy-content h3 {
          font-size: 1.25rem;
          margin-top: 1.2em;
          margin-bottom: 0.5em;
          color: ${t.heading};
          font-weight: 600;
        }
        .privacy-content h4 {
          font-size: 1.1rem;
          margin-top: 1em;
          margin-bottom: 0.5em;
          color: ${t.text};
          font-weight: 600;
        }
        .privacy-content p {
          line-height: 1.8;
          margin-bottom: 1rem;
          color: ${t.text};
        }
        .privacy-content ul,
        .privacy-content ol {
          margin: 1rem 0;
          padding-left: 2rem;
          color: ${t.text};
        }
        .privacy-content li {
          margin: 0.5rem 0;
          line-height: 1.7;
        }
        .privacy-content hr {
          margin: 2rem 0;
          border: none;
          height: 1px;
          background: ${t.border};
        }
        .privacy-content blockquote {
          margin: 1rem 0;
          padding: 1rem 1.5rem;
          background: ${t.code};
          border-left: 4px solid ${t.accent};
          border-radius: 12px;
          font-style: italic;
          color: ${t.textSecondary};
        }
        .privacy-content a {
          color: ${t.accent};
          text-decoration: none;
        }
        .privacy-content a:hover {
          text-decoration: underline;
        }
        .privacy-content strong {
          color: ${t.heading};
          font-weight: 600;
        }
        .privacy-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 1rem 0;
        }
        .privacy-content th,
        .privacy-content td {
          border: 1px solid ${t.border};
          padding: 0.75rem;
          text-align: left;
          color: ${t.text};
        }
        .privacy-content th {
          background: ${t.code};
          font-weight: 600;
        }
        @media (max-width: 768px) {
          .privacy-content h1 {
            font-size: 1.5rem;
          }
          .privacy-content h2 {
            font-size: 1.25rem;
          }
          .privacy-content h3 {
            font-size: 1.1rem;
          }
        }
      `}</style>

      <div
        style={{
          fontFamily: "'Poppins', sans-serif",
          background: t.bg,
          minHeight: "100vh",
        }}
      >
        <Headerpage />

        {/* Hero Section */}
        <section
          style={{
            position: "relative",
            minHeight: "300px",
            padding: "60px 20px",
            textAlign: "center",
            background: `linear-gradient(135deg, #0b0417 0%, #160a2e 40%, ${t.accent} 100%)`,
            color: "#fff",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          {/* Decorative elements */}
          <div
            style={{
              position: "absolute",
              width: "300px",
              height: "300px",
              background: "rgba(168,85,247,0.15)",
              filter: "blur(60px)",
              borderRadius: "50%",
              top: "-150px",
              left: "-100px",
            }}
          />
          <div
            style={{
              position: "absolute",
              width: "250px",
              height: "250px",
              background: "rgba(124,58,237,0.12)",
              filter: "blur(50px)",
              borderRadius: "50%",
              bottom: "-100px",
              right: "-80px",
            }}
          />

          <div
            style={{
              position: "relative",
              zIndex: 2,
              maxWidth: "800px",
            }}
          >
            {/* Thumbnail Image if exists */}
            {privacyData?.page_thumbnail && (
              <div
                style={{
                  marginBottom: "24px",
                }}
              >
                <img
                  src={`${serverURL}/images/${privacyData.page_thumbnail}`}
                  alt={privacyData.page_title}
                  style={{
                    width: "100px",
                    height: "100px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "3px solid white",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </div>
            )}

            <h1
              style={{
                fontSize: "clamp(32px, 5vw, 52px)",
                fontWeight: "700",
                marginBottom: "16px",
              }}
            >
              {privacyData?.page_title || "Privacy Policy"}
            </h1>

            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "center",
                alignItems: "center",
                fontSize: "14px",
                opacity: 0.9,
              }}
            >
              <Shield size={18} />
              <span>Last Updated: {formatDate(privacyData?.updated_at)}</span>
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "center",
                marginTop: "24px",
                flexWrap: "wrap",
              }}
            >
              {[
                { icon: Lock, text: "Secure & Encrypted" },
                { icon: Eye, text: "No Third-Party Sharing" },
                { icon: Database, text: "Full Data Control" },
              ].map((item, index) => (
                <div
                  key={index}
                  style={{
                    background: "rgba(255,255,255,0.15)",
                    backdropFilter: "blur(10px)",
                    padding: "8px 16px",
                    borderRadius: "40px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "13px",
                  }}
                >
                  <item.icon size={16} />
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Main Content Section */}
        <section
          style={{
            padding: "60px 20px",
            position: "relative",
            zIndex: 10,
          }}
        >
          <div
            style={{
              maxWidth: "1100px",
              margin: "0 auto",
            }}
          >
            <div
              style={{
                backgroundColor: t.cardBg,
                borderRadius: "28px",
                border: `1px solid ${t.border}`,
                overflow: "hidden",
                boxShadow: isDark
                  ? "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
                  : "0 25px 50px -12px rgba(0, 0, 0, 0.1)",
              }}
            >
              {/* Content Body */}
              <div style={{ padding: "48px" }}>
                {/* Full HTML Content */}
                <div
                  className="privacy-content"
                  dangerouslySetInnerHTML={{
                    __html: privacyData?.page_description || "No content available",
                  }}
                />

                {/* Footer Info */}
                <div
                  style={{
                    marginTop: "48px",
                    paddingTop: "24px",
                    borderTop: `1px solid ${t.border}`,
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "16px",
                    fontSize: "13px",
                    color: t.textSecondary,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <FileText size={16} />
                    <span>Page ID: {privacyData?.id}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Clock size={16} />
                    <span>Created: {formatDate(privacyData?.created_at)}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <CheckCircle size={16} />
                    <span>Status: {privacyData?.status === 1 ? "Active" : "Inactive"}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Key Privacy Principles Cards */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "24px",
                marginTop: "48px",
              }}
            >
              <div
                style={{
                  background: t.cardBg,
                  padding: "28px",
                  borderRadius: "20px",
                  border: `1px solid ${t.border}`,
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: "70px",
                    height: "70px",
                    background: `${t.accent}15`,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 20px",
                  }}
                >
                  <Lock size={32} color={t.accent} />
                </div>
                <h3 style={{ color: t.heading, fontSize: "18px", marginBottom: "12px" }}>
                  Data Security
                </h3>
                <p style={{ color: t.textSecondary, fontSize: "14px", lineHeight: "1.6" }}>
                  Your personal information is encrypted and stored securely with advanced protection protocols.
                </p>
              </div>

              <div
                style={{
                  background: t.cardBg,
                  padding: "28px",
                  borderRadius: "20px",
                  border: `1px solid ${t.border}`,
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: "70px",
                    height: "70px",
                    background: `${t.accent}15`,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 20px",
                  }}
                >
                  <Eye size={32} color={t.accent} />
                </div>
                <h3 style={{ color: t.heading, fontSize: "18px", marginBottom: "12px" }}>
                  No Third-Party Sharing
                </h3>
                <p style={{ color: t.textSecondary, fontSize: "14px", lineHeight: "1.6" }}>
                  We never sell, trade, or share your personal information with third parties.
                </p>
              </div>

              <div
                style={{
                  background: t.cardBg,
                  padding: "28px",
                  borderRadius: "20px",
                  border: `1px solid ${t.border}`,
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: "70px",
                    height: "70px",
                    background: `${t.accent}15`,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 20px",
                  }}
                >
                  <Database size={32} color={t.accent} />
                </div>
                <h3 style={{ color: t.heading, fontSize: "18px", marginBottom: "12px" }}>
                  Full Data Control
                </h3>
                <p style={{ color: t.textSecondary, fontSize: "14px", lineHeight: "1.6" }}>
                  You have complete control over your data and can request deletion anytime.
                </p>
              </div>
            </div>

            {/* Contact Support CTA */}
            <div
              style={{
                background: `linear-gradient(135deg, ${t.accent}15, ${t.accent}08)`,
                borderRadius: "24px",
                padding: "40px",
                marginTop: "48px",
                textAlign: "center",
                border: `1px solid ${t.border}`,
              }}
            >
              <h3
                style={{
                  color: t.heading,
                  fontSize: "22px",
                  marginBottom: "16px",
                }}
              >
                Have Questions About Your Privacy?
              </h3>
              <p
                style={{
                  color: t.textSecondary,
                  marginBottom: "28px",
                  fontSize: "15px",
                }}
              >
                Our support team is here to help with any privacy concerns or questions
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                <a
                  href="mailto:info@zaptodoor.com"
                  style={{
                    background: t.accent,
                    color: "white",
                    padding: "12px 28px",
                    borderRadius: "40px",
                    textDecoration: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    fontWeight: "500",
                  }}
                >
                  <Mail size={18} />
                  Email Us
                </a>
                <a
                  href="tel:+919200018690"
                  style={{
                    background: "transparent",
                    color: t.text,
                    padding: "12px 28px",
                    borderRadius: "40px",
                    textDecoration: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    border: `1px solid ${t.border}`,
                    fontWeight: "500",
                  }}
                >
                  <Phone size={18} />
                  Call Support
                </a>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default PrivacyPolicy;